import React, { useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { w3cwebsocket } from 'websocket';

import { getIsMsgOutgoing } from 'widget/actions';
import { useRequest } from 'widget/hooks';
import { useWidgetContext } from 'widget/widget-context';
import * as Env from 'widget/env';

import {
  useAccessToken,
  useMessageList,
  useOpenWs,
  useSendMsg,
  useSendReaction,
} from './hooks';
import ChatBodyContext from './chat-body-context';
import Composer from './composer/composer';
import Convo from './convo/convo';

interface ChatBodyProps {
  isChatActive: boolean;
}

const ChatBody = ({ isChatActive }: ChatBodyProps) => {
  const { widget } = useWidgetContext();

  const [isTyping, setIsTyping] = useState(false);
  const [quotedMessage, setQuotedMessage] = useState<Env.QuotedMessage>();

  const [messageList, setMessageList] = useMessageList();
  const { accessToken, createAccessToken, setAccessToken } = useAccessToken();
  const { fetchDialogsRead } = useRequest();

  const { getRootProps, isDragAccept } = useDropzone({
    noClick: true,
    noKeyboard: true,
  });

  const ws = useRef<w3cwebsocket | null>(null);
  const openWs = useOpenWs(ws, { setAccessToken, setIsTyping, setMessageList });
  const sendMsg = useSendMsg(ws, { accessToken, createAccessToken, openWs });
  const sendReaction = useSendReaction(ws);

  useEffect(() => {
    if (!accessToken) return;
    else if (!isChatActive) return;

    const { dialog_id } = messageList.at(-1) || {};
    if (!dialog_id) return;

    const isUnread = messageList.some(
      msg => !getIsMsgOutgoing(accessToken, msg) && msg.status != 'read',
    );

    if (!isUnread) return;
    fetchDialogsRead(accessToken, dialog_id);
  }, [isChatActive, messageList]);

  useEffect(() => {
    if (accessToken) openWs(accessToken);
  }, []);

  const canSendMessage =
    Boolean(accessToken) ||
    widget.is_register_form == false ||
    !(widget.is_email || widget.is_name || widget.is_phone);

  return (
    <ChatBodyContext.Provider
      value={{
        accessToken,
        isDragAccept,
        quotedMessage,
        sendMsg,
        sendReaction,
        setAccessToken,
        setQuotedMessage,
        ws,
      }}
    >
      <div {...getRootProps()} className="whatcrm-widget-chat__body">
        <Convo
          createAccessToken={createAccessToken}
          isTyping={isTyping}
          messageList={messageList}
          openWs={openWs}
        />

        {canSendMessage && <Composer />}
      </div>
    </ChatBodyContext.Provider>
  );
};

export default ChatBody;
